/**
 * Properties Behavior
 *
 * Declares named, typed values (color, size, label, etc.) that consumers
 * reference via `$name` / `{name}` in their own data fields.
 *
 * Resolution follows the CSS `@property` model: each declaration has a
 * `value` and an `inherits` flag. When `inherits: true` (default), ancestor
 * values take precedence. When `inherits: false`, the local value wins.
 * States are local — no ancestor state walk.
 *
 * This behavior is the Babylon.js integration layer for the Canopy resolution
 * engine (PropertyResolver). It handles:
 * - TraceBehavior lifecycle (attach/detach/commit)
 * - Transform overrides (Vector3/Quaternion math)
 * - System value driver polling (per-frame render observer)
 * - External sheet loading
 * - Pseudo-state registration (:selected via RuntimeSelectionManager)
 *
 * All resolution logic (5-step cascade, state stack, inheritance walk,
 * property-driven transitions) is delegated to PropertyResolver.
 */

import { Matrix, Vector3, Quaternion } from '@babylonjs/core/Maths/math.vector';
import { Observable } from '@babylonjs/core/Misc/observable';
import type { Observer } from '@babylonjs/core/Misc/observable';
import type { Scene } from '@babylonjs/core/scene';
import { TraceBehavior } from './tracebehavior';
import type { PropertyProvider, PropertySubscriber, PropertyValue } from './tracebehavior';
import { Logger } from '../utils/Logger';
import { compileExpression, evaluateExpression, EXPRESSION_BUILTINS } from '../core/canopy/ExpressionEvaluator';
import {
  PropertiesData,
} from '../scenedata/behaviors/propertiesdata';
import type { PropertyDeclaration, TransitionConfig, StateDefinition, TransformComponent, TransformOverride, PropertySheet } from '../scenedata/behaviors/propertiesdata';
import { parsePropertySheet, hasLegacyStyleBlock, PROPERTY_SHEET_VERSION } from '../scenedata/behaviors/propertiesdata';
import { hasBoundComponent, isTimeTransition } from '../scenedata/behaviors/propertiesdata';
import { computeDriverProgress } from '../core/canopy/TransitionUtils';
import { getTransitionManager } from '../core/TransitionManager';
import type { PropertyTransition } from '../scenedata/behaviors/propertiesdata';
import type { SceneObject } from '../core/SceneObject';
import { PropertyResolver } from '../core/canopy/PropertyResolver';
import type { ResolverCallbacks } from '../core/canopy/PropertyResolver';
import type { CanopyNodeAdapter, CanopyNodeChild, CanopyTransitionHost, CanopyProvider } from '../core/canopy/types';

// Re-export PropertyValue for consumers that imported it from here
export type { PropertyValue } from './tracebehavior';

/**
 * Structural view of an AnimationSequence exit handle — just enough for the
 * awaited exit phase to play the exit clip without a value import of
 * AnimationSequence (which would cycle through core/SceneObject). Mirrors
 * `AnimationStateHandle` from animationsequence.ts.
 */
interface AnimationExitLike {
  getExit(): { isEmpty: boolean; play(): Promise<void> } | undefined;
}

// Opt-in dangling-state-reference diagnostics. Editor hosts enable this at init
// (via the trace-viewer/editor barrel) so a setState against an undeclared state
// name — the runtime tell-tale of a rename/delete that missed a reference — warns
// instead of silently no-op'ing. OFF by default in production viewers.
let stateDiagnostics = false;

/** Enable/disable dangling-state-reference warnings in {@link Properties.setState}. */
export function setStateDiagnostics(enabled: boolean): void {
  stateDiagnostics = enabled;
}

type Pose = { pos: Vector3; rot: Quaternion; scl: Vector3 };

/** Below this, a re-framed component is the value it landed on. */
const POSE_EPSILON = 1e-6;

/** `pose` as it reads after a change of parent frame (old parent world × new parent world⁻¹). */
function reframePose({ pos, rot, scl }: Pose, change: Matrix): Pose {
  const out = { pos: new Vector3(), rot: new Quaternion(), scl: new Vector3() };
  Matrix.Compose(scl, rot, pos).multiply(change).decompose(out.scl, out.rot, out.pos);
  return out;
}

/**
 * Runtime behavior for managing public template properties.
 * Implements PropertyProvider for the subscription-based notification system.
 *
 * Delegates all resolution logic to PropertyResolver (engine-agnostic).
 * This class provides the Babylon.js integration: transform math,
 * render loop, system values, and TraceBehavior lifecycle.
 */
export class Properties extends TraceBehavior<PropertiesData> implements PropertyProvider {
  static readonly behaviorType = "Properties" as const;

  /** The pure resolution engine (lazily created) */
  private _resolverInstance: PropertyResolver | undefined;

  private get _resolver(): PropertyResolver {
    if (!this._resolverInstance) {
      this._resolverInstance = this._createResolver();
    }
    return this._resolverInstance;
  }

  /** Cached external sheets, keyed by src URL */
  private _loadedSheets: Map<string, PropertySheet> = new Map();

  /** Editor callback — notified when setValue() mutates a property in-place */
  onValueChanged: ((name: string, value: PropertyValue) => void) | null = null;

  /**
   * Multi-listener signal for programmatic `setValue` writes (AutoUI swatch,
   * graph taps, play mode). Unlike the single-slot {@link onValueChanged}, any
   * number of editor observers can subscribe — e.g. to restale state thumbnails
   * that the runtime-tier write would otherwise not announce via commit.
   */
  public onValueChangedObservable = new Observable<{ name: string; value: PropertyValue }>();

  /** Fires when the top (effective) state changes. */
  public onStateChangedObservable = new Observable<{ prev: string | undefined; next: string | undefined }>();

  /** Authored transform captured before state transform override, for revert */
  private _savedTransform: { pos: Vector3; rot: Quaternion; scl: Vector3 } | undefined;
  // Which _savedTransform fields had no authored value and were primed to the
  // neutral pose. Cleared once play-mode entry adopts the live pose for them,
  // and whenever _savedTransform itself is reset.
  private _savedTransformUnauthored: { pos: boolean; rot: boolean; scl: boolean } | undefined;
  // Set by applyCommit: the held baseline predates the committed data.
  private _baselineStale = false;

  /** Cancels the pending deferred deactivation (hide after the transition completes), if one is scheduled. */
  private _deactivationTimer: (() => void) | null = null;

  /** Removal function for :selected RuntimeSelectionManager registration */
  private _removeSelectionRegistration: (() => void) | null = null;

  /**
   * Per-frame render observer for system-value drivers (e.g. camera.distance).
   * System values have no PropertyProvider to subscribe to, so we poll each frame.
   */
  private _systemDriverObserver: Observer<Scene> | null = null;

  /** Active property-driven transform tracking */
  private _transformDriver: {
    config: PropertyTransition;
    fromPos: Vector3; fromRot: Quaternion; fromScl: Vector3;
    toPos: Vector3; toRot: Quaternion; toScl: Vector3;
  } | null = null;

  // ── Resolver setup ──

  private _createResolver(): PropertyResolver {
    return new PropertyResolver(
      this._createNodeAdapter(),
      this._createTransitionHost(),
      this._createCallbacks(),
      () => this.getAuthored(),
    );
  }

  private _createNodeAdapter(): CanopyNodeAdapter {
    return {
      getParentProvider: () => {
        const parent = this.sceneObject.getParent();
        return parent?.getBehavior(Properties)?._resolver ?? null;
      },
      walkAncestorProviders: () => {
        const providers: CanopyProvider[] = [];
        let current = this.sceneObject.getParent();
        while (current) {
          const props = current.getBehavior(Properties);
          if (props) providers.push(props._resolver);
          current = current.getParent();
        }
        return providers;
      },
      getChildren: () => {
        return this.sceneObject.getChildren().map(child => this._wrapChild(child));
      },
      notifyDependentBehaviors: () => {
        this._notifyDependentBehaviors();
      },
    };
  }

  private _wrapChild(child: SceneObject): CanopyNodeChild {
    return {
      getProvider: () => {
        const props = child.getBehavior(Properties);
        return props?._resolver ?? null;
      },
      getChildren: () => {
        return child.getChildren().map(gc => this._wrapChild(gc));
      },
    };
  }

  private _createTransitionHost(): CanopyTransitionHost {
    return {
      startTransition: (name, config, onUpdate, onComplete) => {
        const key = `${this._transitionPrefix}${name}`;
        getTransitionManager(this.scene).start(key, config, onUpdate, onComplete);
      },
      cancelTransition: (name) => {
        getTransitionManager(this.scene).cancel(`${this._transitionPrefix}${name}`);
      },
      hasTransition: (name) => {
        return getTransitionManager(this.scene).has(`${this._transitionPrefix}${name}`);
      },
    };
  }

  private _createCallbacks(): ResolverCallbacks {
    return {
      resolveDriverExpression: (driverName) => {
        return this._resolveDriverExpression(driverName);
      },
      onStateChanged: (prev, next) => {
        this.onStateChangedObservable.notifyObservers({ prev, next });
      },
      applyStateTransform: (state, hadPrevious, transition, enterFromOverride) => {
        this._applyStateTransform(state, hadPrevious, transition, enterFromOverride);
      },
      applyStateActive: (desiredActive, transition) => {
        this._applyStateActive(desiredActive, transition);
      },
      setupTransformDriver: (state, config) => {
        this._setupTransformDriver(state, config);
      },
      teardownTransformDriver: () => {
        this._transformDriver = null;
      },
      onTransformDriverValueChanged: (_driverName, _driverValue) => {
        this._updateTransformFromDriver();
      },
      getHostActive: () => {
        return this.sceneObject.active;
      },
    };
  }

  // ── Transition key prefix ──

  private get _transitionPrefix(): string {
    return `props:${this.sceneObject.transform.uniqueId}:`;
  }

  // ── Lifecycle ──

  init(): void {
    this._resolver.rebuildPropertyMap(
      this.getAuthored().properties ?? [],
      this._getSheetDeclarations(),
    );
  }

  async load(): Promise<void> {
    await this._loadExternalSheets();
    this._resolver.rebuildPropertyMap(
      this.getAuthored().properties ?? [],
      this._getSheetDeclarations(),
    );
  }

  protected onAttach(): void {
    this._resolver.rebuildPropertyMap(
      this.getAuthored().properties ?? [],
      this._getSheetDeclarations(),
    );
    this._installSystemDriverObserver();
    this._updateSelectionRegistration();
    this._applyBaseTransform();
    this._notifyDependentBehaviors();
  }

  protected onDetach(): void {
    this._resolver.stopAllTransitions();
    this._uninstallSystemDriverObserver();

    if (this._removeSelectionRegistration) {
      this._removeSelectionRegistration();
      this._removeSelectionRegistration = null;
    }

    this._cancelDeactivation();
    this.sceneObject.setActiveOverride(undefined);

    this._resolver.dispose();
    this._loadedSheets.clear();
    this.onValueChanged = null;
    this.onValueChangedObservable.clear();
    this.onStateChangedObservable.clear();
    this._savedTransform = undefined;
    this._savedTransformUnauthored = undefined;
    this._notifyDependentBehaviors();
  }

  protected async applyCommit(_patch: Partial<PropertiesData>): Promise<void> {
    await this._loadExternalSheets();

    // The held baseline follows the new data, refreshed once the resolver
    // holds the new declarations (a bound base transform reads them) and
    // before it re-poses a kept state (re-priming instead would bake a
    // posed node into the rest pose in Play).
    this._baselineStale = true;
    this._cancelDeactivation();

    const { namesChanged } = this._resolver.handleCommit(
      this.getAuthored().properties ?? [],
      this._getSheetDeclarations(),
    );
    this._refreshStaleBaseline();

    // No state survived: restore the transform and active any override
    // (state or dormant contribution) drove. A held baseline is the signal —
    // only an override primes it.
    if (this._resolver.getStateStack().length === 0) {
      if (this._savedTransform) {
        const { pos, rot, scl } = this._savedTransform;
        this.sceneObject.setTransform({ position: pos, rotation: rot, scale: scl }, null);
      }
      this.sceneObject.setActiveOverride(undefined);
      this._savedTransform = undefined;
      this._savedTransformUnauthored = undefined;
    }

    if (namesChanged) {
      this._notifyDependentBehaviors();
    }

    this._updateSelectionRegistration();
    this._applyBaseTransform();

    // Notify editor that property data changed (e.g. so the sandbox panel refreshes).
    if (this.onValueChanged && _patch.properties) {
      for (const decl of _patch.properties) {
        this.onValueChanged(decl.name, decl.value);
      }
    }
  }

  /**
   * A bound base transform tracks its driver in the SAME tick — and a
   * re-resolve that changes nothing but that transform is NOT a commit.
   *
   * The base-class path re-resolves and hands every changed field to the
   * async `applyCommit`. For a bound pose that is wrong twice over: the pose
   * would land a microtask late (a visible frame of lag under a slider), and
   * `applyCommit` re-resolves the kept states and stops their transitions.
   * CANOPY_STATES §5.1.1 promises the pose "re-applies on change"; it must do
   * so without disturbing the states.
   *
   * So: when the transform is the only resolved field that changed, apply it
   * and stop. Anything else (a sheet URL, a declaration bound to a property)
   * still takes the commit path, followed by the same immediate re-apply.
   */
  override _onPropertyChanged(_name: string): void {
    const { patch, hasChanges } = this._reresolve();
    const changed = Object.keys(patch);
    if (changed.length === 1 && changed[0] === 'transform') {
      this._applyBaseTransform();
      return;
    }
    if (hasChanges) this._applyResolvedPatch(patch);
    this._applyBaseTransform();
  }

  protected onPlayModeChanged(playing: boolean): void {
    if (playing) {
      // Before any play-mode state applies: fields the AUTHORED transform
      // never set take their rest pose from the LIVE node. Authored-unset
      // is not "the origin" — it is "never committed", and for an object
      // placed at runtime (import, tools, an unsaved session) the live node
      // is the only place its pose exists. Without this, every transform
      // state/transition baselines those fields at zero/identity, so page
      // enter/exit animations fly and scale objects from the ROOT pivot
      // instead of in place. Editor-side priming is deliberately untouched:
      // there authored-or-origin is load-bearing (a gizmo-moved node must
      // not leak into Base via captureTransform — see
      // properties-prime-saved-transform.test.ts).
      this._adoptLiveRestPoseForUnauthoredFields();
      this._resolver.applyInitialState();
    } else {
      // TraceScene's play-off sweep has reset the states and stopped the
      // transitions — but NOT runtime overrides, which a commit keeps too
      // while their declaration is unchanged, and play never edits
      // declarations. setValue picks made during play (trace/setProperty)
      // are exactly such overrides, so they must be dropped here or they
      // survive Stop.
      // This also drops setValue picks made BEFORE play (e.g. variant
      // previews) — accepted: previews are runtime-ephemeral by design, and
      // a Play/Stop round-trip returning to authored state is the contract.
      this._resolver.clearRuntimeOverrides();
    }
  }

  // ── PropertyProvider interface (delegates to resolver) ──

  getValue(name: string, settled = false): PropertyValue | undefined {
    return this._resolver.getValue(name, settled);
  }

  hasProperty(name: string): boolean {
    return this._resolver.hasProperty(name);
  }

  subscribe(name: string, subscriber: PropertySubscriber): void {
    // PropertySubscriber and CanopySubscriber have the same shape
    this._resolver.subscribe(name, subscriber);
  }

  unsubscribe(name: string, subscriber: PropertySubscriber): void {
    this._resolver.unsubscribe(name, subscriber);
  }

  unsubscribeAll(subscriber: PropertySubscriber): void {
    this._resolver.unsubscribeAll(subscriber);
  }

  // ── Public API (delegates to resolver) ──

  getProperty(name: string): PropertyDeclaration | undefined {
    return this._resolver.getProperty(name);
  }

  getPropertyNames(): string[] {
    return this._resolver.getPropertyNames();
  }

  /**
   * Every distinct value a property can take from its declaration and
   * authored states: the declared base value plus each state's override,
   * deduplicated, in authoring order.
   *
   * Consumers bound to asset-holding properties use this to prefetch — e.g.
   * Renderer warms the GLB container cache for a bound `materials[].src` so
   * state-driven material swaps don't hitch on first activation. Runtime
   * contributions (Keyframes) are not included.
   */
  getDeclaredValues(name: string): PropertyValue[] {
    const values: PropertyValue[] = [];
    const seen = new Set<string>();
    const add = (value: PropertyValue): void => {
      // Vec values are arrays — key by JSON so equal vectors dedupe too
      const key = typeof value === 'object' ? JSON.stringify(value) : `${typeof value}:${String(value)}`;
      if (seen.has(key)) return;
      seen.add(key);
      values.push(value);
    };
    const decl = this._resolver.getProperty(name);
    if (decl) add(decl.value);
    const states = this.getAuthored().states;
    if (states) {
      for (const stateDef of Object.values(states)) {
        if (stateDef.values && Object.hasOwn(stateDef.values, name)) {
          add(stateDef.values[name]);
        }
      }
    }
    return values;
  }

  getProperties(): PropertyDeclaration[] {
    return this._resolver.getProperties();
  }

  getLabeledProperties(): PropertyDeclaration[] {
    return this._resolver.getLabeledProperties();
  }

  /**
   * Get all declarations loaded from external sheets.
   * Used by the editor to show sheet-sourced declarations as read-only.
   */
  getSheetDeclarations(): PropertyDeclaration[] {
    return this._getSheetDeclarations();
  }

  setValue(name: string, value: PropertyValue, transition?: TransitionConfig): boolean {
    if (!this._resolver.setValue(name, value, transition)) return false;
    this.onValueChanged?.(name, value);
    this.onValueChangedObservable.notifyObservers({ name, value });
    return true;
  }

  /**
   * Drop all runtime `setValue` overrides and re-resolve to the state stack /
   * authored value. Used by the editor to return to authored state (e.g. on play
   * mode exit — picks made via AutoUI during play must not persist).
   */
  clearRuntimeOverrides(): void {
    this._resolver.clearRuntimeOverrides();
  }

  /**
   * Cancel every in-flight property transition through the resolver, draining
   * its bookkeeping: transition overlay values, active-key set, property
   * drivers, and any pending {@link setStateAwaited} barriers. TraceScene's
   * play-off sweep calls this per object BEFORE its blunt
   * `TransitionManager.cancelAll()` — a manager-level cancel fires no
   * callbacks, so without this the resolver would stay frozen mid-fade
   * (`isTransitioning` true forever, barriers never settling) for consumers
   * that don't follow Stop with a restore commit (viewer-app, MCP, headless).
   */
  stopAllTransitions(): void {
    this._resolver.stopAllTransitions();
  }

  isTransitioning(name: string): boolean {
    return this._resolver.isTransitioning(name);
  }

  get activeTransitionCount(): number {
    return this._resolver.activeTransitionCount;
  }

  // ── External transition overlay (used by Pages match transitions) ──

  /** @internal */
  _setTransitionValue(name: string, value: PropertyValue): void {
    this._resolver.setTransitionValue(name, value);
  }

  /** @internal */
  _clearTransitionValue(name: string): void {
    this._resolver.clearTransitionValue(name);
  }

  // ── State API (delegates to resolver) ──

  /** The state that shows: the editor's pick when one is set, else the runtime slot's. */
  getActiveState(): string | undefined {
    return this._resolver.getActiveState();
  }

  /**
   * The runtime slot's state, whatever an editor pick shows. Code that saves
   * a state and `setState`s it back reads this, so it never writes a pick
   * into the runtime slot.
   */
  getRuntimeState(): string | undefined {
    return this._resolver.getRuntimeState();
  }

  /** The editor's pick: a state name, null for Base, undefined for none. */
  getEditorState(): string | null | undefined {
    return this._resolver.getEditorState();
  }

  /**
   * Show an editor pick over what drivers set (Pages, interactions,
   * `setState`), which keeps updating underneath: a custom state, a
   * pseudo-state alone (`:hover`) or both (`X:hover`); null shows Base, and
   * undefined hands back to the runtime slot. Picks are never saved, survive
   * commits while their state exists, and clear when Play starts or stops.
   */
  setEditorState(pick: string | null | undefined, instant = false): void {
    this._resolver.setEditorState(pick, instant);
  }

  getActiveStates(): string[] {
    return this._resolver.getActiveStates();
  }

  hasState(name: string): boolean {
    return this._resolver.hasState(name);
  }

  getStateTransition(name: string): TransitionConfig | undefined {
    return this._resolver.getStateTransition(name);
  }

  getStateNames(): string[] {
    return this._resolver.getStateNames();
  }

  /**
   * Whether this object carries the `stateGroup` marker — i.e. it OWNS its state
   * group (is a group boundary). Read directly off authored data — no clone — so
   * the group boundary predicates can be called in tree walks cheaply. This marker
   * is RUNTIME-load-bearing: a scoped `trace/setState` prunes its walk at a
   * boundary (STATE_SCOPE_SPEC.md, core/canopy/stateScope.ts `isScopeBoundary`).
   * See PropertiesData.stateGroup and docs-internal/STATE_GROUP_BOUNDARIES.md.
   */
  isStateGroupOwner(): boolean {
    return this.getAuthored().stateGroup === true;
  }

  hasPseudoStates(): boolean {
    return this._resolver.hasPseudoStates();
  }

  /**
   * Activate a custom state by name. **Passing `undefined` clears the active
   * custom state** (reverting to base / lower-priority contributions) — this is a
   * stable part of the contract, relied on by callers that snapshot
   * `getActiveState()` (→ `string | undefined`) and restore it later. `instant`
   * snaps without transition.
   */
  /**
   * Awaited variant of {@link setState} (Pages spec P-18 — the shared "awaited
   * state exit phase" primitive). Applies the state change, then resolves once
   * both of the following have played out:
   *
   * 1. every property transition the change *started* (delegated to the
   *    resolver's {@link PropertyResolver.setStateAwaited}), and
   * 2. any exit clip on a sibling AnimationSequence on this same object — a
   *    revert that clears a bound `src` hard-stops the clip, so on an awaited
   *    deactivate we explicitly play the authored `exit` clip and await it,
   *    giving state reverts a real exit phase instead of an instant cut.
   *
   * Opt-in and additive: `setState` is unchanged. Callers that don't need to
   * sequence after the exit keep using `setState`; only Pages' deferred
   * deactivation consumes this today. When nothing animates and there is no
   * exit clip, the returned Promise is already resolved.
   *
   * The exit clip is only played on a *deactivating* change — clearing the
   * active state (`name === undefined`) or switching to a state that turns the
   * object off. Activating/switching-to-a-visible state does not trigger it.
   */
  async setStateAwaited(name: string | undefined, instant = false, transitionFallback?: TransitionConfig): Promise<void> {
    const propsDone = this._resolver.setStateAwaited(name, instant, transitionFallback);
    const exitDone = instant ? undefined : this._playExitClipIfDeactivating(name);
    if (exitDone) {
      await Promise.all([propsDone, exitDone]);
    } else {
      await propsDone;
    }
  }

  /**
   * If this object has a sibling AnimationSequence with an authored `exit`
   * clip, and the pending state change deactivates the object (either clearing
   * the active state or entering a state whose `active === false`), play the
   * exit clip and return its completion Promise. Otherwise undefined.
   *
   * Uses the string `getBehavior` overload (not the typed one) deliberately: a
   * value import of AnimationSequence here would create a
   * behaviors → animationsequence → core → behaviors cycle. The handle is read
   * structurally through {@link AnimationExitLike}.
   */
  private _playExitClipIfDeactivating(name: string | undefined): Promise<void> | undefined {
    // Determine whether the incoming state deactivates the object. Clearing the
    // custom state reverts to base (active unchanged → not a deactivate unless
    // the base is inactive, which the exit phase does not special-case). A named
    // state with `active === false` is the deactivate signal.
    const states = this.getAuthored().states;
    const targetActive = name !== undefined ? states?.[name]?.active : undefined;
    if (targetActive !== false) return undefined;

    const animSeq = this.sceneObject.getBehavior('AnimationSequence') as unknown as AnimationExitLike | null;
    const exit = animSeq?.getExit?.();
    if (!exit || exit.isEmpty) return undefined;
    return exit.play();
  }

  setState(name: string | undefined, instant = false, transitionFallback?: TransitionConfig): void {
    if (stateDiagnostics && name !== undefined && !this.hasState(name)) {
      // Flag-gated (editor hosts opt in via setStateDiagnostics). A setState with an
      // undeclared name is a silent runtime no-op — the tell-tale of a dangling
      // reference left by a rename/delete that missed a ref. Off by default: runtime
      // paths (Pages, TraceSetStateBlock) pass authored names through and legacy
      // content has historic dangles, so an unconditional warn would be noisy.
      Logger.warn(
        `[Properties] setState("${name}") ignored on ${this.sceneObject.getPath()} — state not declared (dangling reference?)`,
      );
    }
    this._resolver.setState(name, instant, transitionFallback);
  }

  /** Snap to Base: the editor's pick, the custom state and every pseudo-state clear in one step. Contributions stay. */
  resetStates(): void {
    this._resolver.resetStates();
  }

  setPseudoState(name: string, active: boolean, instant = false): void {
    this._resolver.setPseudoState(name, active, instant);
  }

  /**
   * Push an external state contribution into this Properties resolver.
   * Used by the Keyframes behavior (and any future thin-driver behavior) to
   * inject named state overrides without baking them into authored data.
   *
   * `contributorId` is a stable identifier owned by the caller (e.g. the
   * Keyframes behavior's transform.uniqueId). Multiple contributors can each
   * push state at the same node — they stack at custom-state priority and
   * later pushes win ties.
   */
  pushContribution(
    contributorId: number,
    name: string,
    def: StateDefinition,
    instant = false,
    transitionFallback?: TransitionConfig,
  ): void {
    this._resolver.pushContribution(contributorId, name, def, instant, transitionFallback);
  }

  /** Clear a specific external contribution. No-op if not present. */
  clearContribution(
    contributorId: number,
    name: string,
    instant = false,
    transitionFallback?: TransitionConfig,
  ): void {
    this._resolver.clearContribution(contributorId, name, instant, transitionFallback);
  }

  /** Clear all contributions from a given contributor. */
  clearAllContributions(
    contributorId: number,
    instant = false,
    transitionFallback?: TransitionConfig,
  ): void {
    this._resolver.clearAllContributions(contributorId, instant, transitionFallback);
  }

  /**
   * Atomically replace one contribution with another in a single recompute.
   * Used by Keyframes when switching between two keyframes that both target
   * the same path — avoids a clear+push sequence that would briefly snap back
   * to authored before animating to the new target.
   */
  replaceContribution(
    contributorId: number,
    oldName: string,
    newName: string,
    def: StateDefinition,
    instant = false,
    transitionFallback?: TransitionConfig,
  ): void {
    this._resolver.replaceContribution(contributorId, oldName, newName, def, instant, transitionFallback);
  }

  resetSavedTransform(): void {
    this._savedTransform = undefined;
    this._savedTransformUnauthored = undefined;
  }

  /**
   * Snapshot the AUTHORED (sceneData) transform as the saved-transform baseline,
   * if not already primed. Use before applying a state transform to an object
   * whose live node may have been moved (e.g. a freshly-dragged child being seeded
   * into a parent's state): without this, an intervening `captureTransform()`
   * (e.g. a panel refresh on a behavior-changed event) would read the live, moved
   * node as authored and bake the move into Base. Priming from authored ensures
   * `getAuthoredTransform()` returns the true rest pose during that window.
   */
  primeSavedTransform(): void {
    this._ensureSavedTransform();
  }

  /**
   * Set fields of the held baseline, if one is held, for a runtime writer
   * that derives them anew on every pass (layout's solved position and
   * scale). The baseline was primed from authored data at some earlier
   * point, so it can hold a number the writer has since replaced; the live
   * pose is no substitute, because a state may be posing it. Without a
   * baseline there is nothing to do: the next priming reads authored data,
   * which the writer keeps current outside Play.
   */
  setRestPose(pose: { position?: Vector3; scale?: Vector3 }): void {
    if (!this._savedTransform) return;
    if (pose.position) {
      this._savedTransform.pos = pose.position.clone();
      if (this._savedTransformUnauthored) this._savedTransformUnauthored.pos = false;
    }
    if (pose.scale) {
      this._savedTransform.scl = pose.scale.clone();
      if (this._savedTransformUnauthored) this._savedTransformUnauthored.scl = false;
    }
  }

  /**
   * Keep the held Default pose where it is in the world, as the move kept the
   * node, and in edit mode each authored pose over it as well: every state's
   * `transform` and `enterFrom`, and the base `transform`. Without this,
   * saving after the move writes them in the frame of the parent the object
   * left. The data is replaced in place rather than committed: the live pose
   * is already right, and a commit would drop the state that shows.
   */
  override onParentFrameChanged(from: Matrix, to: Matrix): void {
    const change = from.multiply(Matrix.Invert(to));
    const authored = this.getAuthored();
    const reframesData = !this.playMode && (!!authored.transform
      || Object.values(authored.states ?? {}).some(def => def.transform || def.enterFrom));
    // The authored data is still in the old frame here, so priming reads it.
    if (reframesData) this._ensureSavedTransform();
    if (!this._savedTransform) return;
    const before = this._savedTransform;
    const after = reframePose(before, change);
    this._savedTransform = after;
    if (!reframesData) return;

    const states: Record<string, StateDefinition> = {};
    for (const [name, def] of Object.entries(authored.states ?? {})) {
      const transform = Properties._reframeOverride(def.transform, before, after, change);
      // `enterFrom` sits over the state's own pose; with a bound pose under it
      // there is no number to change frames with.
      const enterFrom = def.transform && hasBoundComponent(def.transform)
        ? def.enterFrom
        : Properties._reframeOverride(def.enterFrom, Properties._overridePose(before, def.transform),
          Properties._overridePose(after, transform), change);
      states[name] = { ...def, ...(transform && { transform }), ...(enterFrom && { enterFrom }) };
    }
    this._replaceAuthored({
      ...authored,
      ...(authored.states && { states }),
      ...(authored.transform && { transform: Properties._reframeOverride(authored.transform, before, after, change) }),
    });
  }

  /**
   * Get the authored (pre-state-override) transform if this Properties is
   * currently driving a transform override; otherwise undefined.
   *
   * Callers that need to serialize "what the user authored" (export,
   * captureTransform, undo snapshots) should consult this — the live Babylon
   * transform may be the authored pose with a state/contribution delta on top,
   * and serializing that would bake the delta into authored data.
   */
  getAuthoredTransform(): { position: Vector3; rotation: Quaternion; scale: Vector3 } | undefined {
    if (!this._savedTransform) return undefined;
    return {
      position: this._savedTransform.pos.clone(),
      rotation: this._savedTransform.rot.clone(),
      scale: this._savedTransform.scl.clone(),
    };
  }

  /**
   * The authored active flag (after `$ref` resolution), whatever a state or
   * keyframe shows. `sceneObject.active` carries the override instead.
   */
  getAuthoredActive(): boolean {
    return this.sceneObject.authoredActive;
  }

  public override onPointerEvent(type: 'select' | 'hover' | 'active', isEnter: boolean): void {
    if (type === 'hover') {
      this.setPseudoState(':hover', isEnter);
    } else if (type === 'active') {
      this.setPseudoState(':active', isEnter);
    }
  }

  // ── Internal: property map access for cascade (used by resolver) ──

  /** @internal — used by PropertyResolver cascade check via CanopyNodeChild */
  get propertyMap(): Map<string, PropertyDeclaration> {
    return this._resolver.getPropertyMap();
  }

  // ── External sheet loading ──

  /**
   * Sheet declarations in AUTHORED order, not cache order.
   *
   * Precedence among sheets is first-wins (see PropertyResolver.
   * rebuildPropertyMap), so this order decides which sheet supplies a name
   * two sheets both declare. `_loadedSheets` is keyed by src and skips
   * already-cached entries on reload, so its insertion order is the order the
   * sheets were FIRST seen — reordering `sheets` afterwards left the map, and
   * therefore precedence, on the stale sequence.
   */
  private _getSheetDeclarations(): PropertyDeclaration[] {
    const result: PropertyDeclaration[] = [];
    for (const src of this.getAuthored().sheets ?? []) {
      const sheet = this._loadedSheets.get(src);
      if (sheet?.declarations) result.push(...sheet.declarations);
    }
    return result;
  }

  /**
   * The sheets this object references, in authored order.
   *
   * Index 0 is the style slot by editor convention (see `PropertiesData.sheets`
   * and `docs/STYLES.md`) — the applied style is this stored URL, not something
   * derived from the values it resolves to.
   */
  getSheetUrls(): readonly string[] {
    return this.getAuthored().sheets ?? [];
  }

  /**
   * The parsed sheet behind a referenced URL, if it loaded.
   *
   * Exposed for editors: a style picker shows the applied style's `label`, and
   * publish-flatten reads the declarations a sheet contributed. Returns null
   * for a URL this object does not reference, or one that failed to load.
   */
  getLoadedSheet(src: string): PropertySheet | null {
    return this._loadedSheets.get(src) ?? null;
  }

  private async _loadExternalSheets(): Promise<void> {
    const authored = this.getAuthored();
    const sheetUrls = authored.sheets ?? [];

    const referencedSrcs = new Set(sheetUrls);
    for (const cachedSrc of this._loadedSheets.keys()) {
      if (!referencedSrcs.has(cachedSrc)) {
        this._loadedSheets.delete(cachedSrc);
      }
    }

    for (const src of sheetUrls) {
      if (this._loadedSheets.has(src)) continue;
      try {
        const sheet = await this._fetchSheet(src);
        if (sheet) {
          this._loadedSheets.set(src, sheet);
        } else {
          Logger.warn(`[Properties] Failed to parse sheet: ${src}`);
        }
      } catch (err) {
        Logger.warn(`[Properties] Failed to load sheet: ${src}`, err);
      }
    }
  }

  private async _fetchSheet(src: string): Promise<PropertySheet | null> {
    const blob = this.sceneObject.resources.getAsset(src);
    if (blob) {
      const text = await blob.text();
      return Properties._parseSheet(text);
    }
    // A folder document's sheet is one of its files: fetch it from the
    // document's folder, not the page origin (spec §2.3).
    const response = await fetch(this.sceneObject.resources.resolveAssetUrl(src) ?? src);
    if (!response.ok) return null;
    const text = await response.text();
    return Properties._parseSheet(text);
  }

  /**
   * Accepts both sheet forms — the versioned envelope and the bare
   * `PropertyDeclaration[]` that predates it. `parsePropertySheet` decides
   * which; this adds the per-declaration shape check the loader has always
   * applied, so a malformed sheet is rejected whole rather than half-loaded.
   */
  private static _parseSheet(text: string): PropertySheet | null {
    try {
      const raw: unknown = JSON.parse(text);
      const sheet = parsePropertySheet(raw);
      if (!sheet) {
        // Distinguish "written by a build we don't know" from garbage — the
        // former is the signal a migration needs, the latter is a bad file.
        const version = (raw as { version?: unknown } | null)?.version;
        if (typeof version === 'number' && version !== PROPERTY_SHEET_VERSION) {
          Logger.warn(`[Properties] Sheet version ${version} is not supported (this build reads ${PROPERTY_SHEET_VERSION}) — sheet ignored`);
        }
        return null;
      }
      // Deliberately unknown-typed: parsePropertySheet asserts the shape it
      // cannot verify, so this is the only place the contents are actually
      // checked.
      const declarations: unknown[] = sheet.declarations ?? [];
      for (const item of declarations) {
        if (typeof item !== 'object' || item === null) return null;
        if (typeof (item as Record<string, unknown>).name !== 'string') return null;
        if (typeof (item as Record<string, unknown>).type !== 'string') return null;
      }
      // A sheet whose content is a `styles` bundle contributes nothing now that
      // a style IS a sheet. Say so: silently loading it as empty would present
      // as "the theme did nothing" with no way to tell that from a sheet that
      // genuinely declares nothing.
      if (declarations.length === 0 && hasLegacyStyleBlock(raw)) {
        Logger.warn(
          '[Properties] Sheet carries a `styles` bundle and no declarations — that shape is no longer read. ' +
          'A style is a sheet: convert each style to a sheet whose `declarations` hold its values.',
        );
      }
      return sheet;
    } catch {
      return null;
    }
  }

  // ── :selected registration ──

  private _updateSelectionRegistration(): void {
    const needsSelection = this._resolver.hasPseudoReference(':selected');

    if (needsSelection && !this._removeSelectionRegistration) {
      let manager;
      try { manager = this.sceneObject.traceScene.getRuntimeSelectionManager(); } catch { return; }
      this._removeSelectionRegistration = manager.register(
        this.sceneObject,
        {
          onSelected: () => { if (this.sceneObject.traceScene.isPlayMode()) this.setPseudoState(':selected', true); },
          onDeselected: () => { if (this.sceneObject.traceScene.isPlayMode()) this.setPseudoState(':selected', false); },
        },
      );
    } else if (!needsSelection && this._removeSelectionRegistration) {
      this._removeSelectionRegistration();
      this._removeSelectionRegistration = null;
    }
  }

  // ── System driver polling (Babylon render loop) ──
  //
  // A single per-frame observer is installed in onAttach and removed in onDetach.
  // It unconditionally ticks the resolver's system drivers; when no drivers are
  // active the resolver's onDriverValueChanged short-circuits on empty targets,
  // so the cost is a handful of map lookups. Keeping the observer permanent
  // means no entry point (setState, setPseudoState, applyInitialState, …) needs
  // to remember to sync lifecycle — the class of "forgot to wire the observer"
  // regression is structurally impossible.

  private _installSystemDriverObserver(): void {
    if (this._systemDriverObserver) return;
    this._systemDriverObserver = this.scene.onBeforeRenderObservable.add(() => {
      // System-value drivers (camera.distance, time.elapsed, …) only tick in
      // play mode. In edit mode _resolveDriverExpression also returns undefined,
      // so even if this guard were removed the tick would be a no-op — but we
      // early-return here to skip the per-frame iteration cost entirely.
      if (!this.sceneObject.traceScene.isPlayMode()) return;
      for (const driverName of this._resolver.getSystemDriverNames()) {
        this._resolver.onDriverValueChanged(driverName);
      }
      if (this._transformDriver) {
        this._updateTransformFromDriver();
      }
    });
  }

  private _uninstallSystemDriverObserver(): void {
    if (!this._systemDriverObserver) return;
    this.scene.onBeforeRenderObservable.remove(this._systemDriverObserver);
    this._systemDriverObserver = null;
  }

  // ── Driver expression resolution (system values) ──

  private _resolveDriverExpression(driverName: string): number | undefined {
    // In edit mode, system-value drivers (camera.distance, time.elapsed, …)
    // return undefined → resolver falls through to NaN → computeDriverProgress
    // returns 1 → state previews at its target. This matches how non-driven
    // state previews already work (you see the state fully applied) and keeps
    // live runtime values from animating the cube while the creator is editing.
    // Local-property drivers resolve earlier in _resolveDriverValue and still
    // work in edit mode, since those are authored, not live.
    if (!this.sceneObject.traceScene.isPlayMode()) return undefined;

    const fn = compileExpression(driverName);
    if (!fn) return undefined;

    const systemValues = this.sceneObject.traceScene?.getSystemValues();
    if (!systemValues) return undefined;

    const scope: Record<string, unknown> = {
      ...systemValues.buildScope(this.sceneObject.transform),
      ...EXPRESSION_BUILTINS,
    };

    const result = evaluateExpression(fn, scope);
    return typeof result === 'number' ? result : undefined;
  }

  // ── Transform overrides (Babylon-specific Vector3/Quaternion math) ──

  private _ensureSavedTransform(): void {
    this._refreshStaleBaseline();
    if (this._savedTransform) return;
    const { pose, unauthored } = this._restPoseFromData();
    this._savedTransform = pose;
    this._savedTransformUnauthored = unauthored;
    // Already playing (first override arrives mid-play, e.g. a Pages state
    // on a runtime-placed object): the play-entry hook has run, so adopt now.
    if (this.playMode) this._adoptLiveRestPoseForUnauthoredFields();
  }

  /**
   * The rest pose the data defines, and which of its fields the data never
   * set. Read from scene data, not the live node: the live pose may carry a
   * state override or (in editor contexts) a gizmo move not yet folded into
   * authored data, and using it as the baseline would double-apply deltas.
   * A base `transform` sits between authored and states: it is what a state
   * reverts TO, so the baseline is the overlaid pose. A base transform
   * supplying a component counts as authored for that component.
   */
  private _restPoseFromData(): { pose: { pos: Vector3; rot: Quaternion; scl: Vector3 }; unauthored: { pos: boolean; rot: boolean; scl: boolean } } {
    const t = this.sceneObject.getSceneObjectData().transform;
    const p = t?.translation;
    const r = t?.rotation;
    const s = t?.scale;
    const authored = {
      pos: p ? new Vector3(p[0], p[1], p[2]) : Vector3.Zero(),
      rot: r ? new Quaternion(r[0], r[1], r[2], r[3]) : Quaternion.Identity(),
      scl: s ? new Vector3(s[0], s[1], s[2]) : new Vector3(1, 1, 1),
    };
    const base = this._resolvedBaseTransform();
    const overlaid = base ? Properties._computeTransformTargets(authored, base) : undefined;
    return {
      pose: overlaid ? { pos: overlaid.toPos, rot: overlaid.toRot, scl: overlaid.toScl } : authored,
      unauthored: { pos: !p && !base?.position, rot: !r && !base?.rotation, scl: !s && !base?.scale },
    };
  }

  private _refreshStaleBaseline(): void {
    if (!this._baselineStale) return;
    this._baselineStale = false;
    this._refreshSavedTransform();
  }

  /**
   * Bring a held baseline up to date with the data. Once Play has adopted
   * the live rest pose for the fields the data never set, those fields keep
   * it: a posed node can't give it again.
   */
  private _refreshSavedTransform(): void {
    const held = this._savedTransform;
    if (!held) return;
    const { pose, unauthored } = this._restPoseFromData();
    const adopted = this._savedTransformUnauthored === undefined;
    this._savedTransform = {
      pos: adopted && unauthored.pos ? held.pos : pose.pos,
      rot: adopted && unauthored.rot ? held.rot : pose.rot,
      scl: adopted && unauthored.scl ? held.scl : pose.scl,
    };
    if (!adopted) this._savedTransformUnauthored = unauthored;
  }

  /**
   * Play-mode rest-pose correction. `_savedTransform` fields that had no
   * authored value were primed to zero/identity — correct for the editor's
   * captureTransform contract, wrong as an animation baseline: a state
   * transition would move/scale the object from the root pivot. At play entry
   * the live node holds the object's true pose (any editor-time override has
   * not been re-applied yet in play), so adopt it for exactly the unauthored
   * fields. Authored fields are never touched. Idempotent per priming.
   */
  private _adoptLiveRestPoseForUnauthoredFields(): void {
    if (!this._savedTransform || !this._savedTransformUnauthored) return;
    const un = this._savedTransformUnauthored;
    if (!un.pos && !un.rot && !un.scl) return;
    const node = this.sceneObject.transform;
    if (un.pos) this._savedTransform.pos = node.position.clone();
    if (un.rot) {
      this._savedTransform.rot =
        node.rotationQuaternion?.clone() ??
        Quaternion.FromEulerAngles(node.rotation.x, node.rotation.y, node.rotation.z);
    }
    if (un.scl) this._savedTransform.scl = node.scaling.clone();
    this._savedTransformUnauthored = undefined;
  }

  /**
   * The base `transform` with its bindings resolved.
   *
   * Resolved on demand rather than read off `_resolved`: a base transform
   * routinely binds a property this same behavior declares, and `_resolved`
   * is computed in the constructor — before `onAttach` builds the resolver
   * map that makes such a property resolvable at all.
   */
  private _resolvedBaseTransform(): TransformOverride | undefined {
    const authored = this.getAuthored().transform;
    if (!authored) return undefined;
    return TraceBehavior._resolveValue(authored, this.sceneObject) as TransformOverride;
  }

  /**
   * Write the base `transform` pose — the object's own pose with any bound
   * components resolved.
   *
   * Runs on attach and whenever a subscribed property changes. A state
   * transform outranks it while ANY state carrying one is active (the stack
   * merges transform per component, PropertyResolver): the write is skipped,
   * but the saved baseline is refreshed so the eventual revert lands on the
   * current bound pose rather than a stale one.
   */
  private _applyBaseTransform(): void {
    // Presence gate before any work: this runs on every subscribed property
    // change, and most Properties behaviors have no base transform at all.
    if (!this.getAuthored().transform) return;

    // Bring the baseline up to the freshly resolved override.
    this._refreshSavedTransform();
    this._ensureSavedTransform();

    // Anything active that owns a transform outranks the base write. The
    // resolver already answers this from its eligible set — which is where
    // compound states (`moved:hover`) are synthesized and where keyframe
    // contributions live; neither ever sits on the state stack, so a guard
    // reading the stack missed both. Consulting the top state alone wrote
    // the base pose on hover (#748).
    if (this._resolver.hasActiveTransformOverride) return;

    const { pos, rot, scl } = this._savedTransform!;
    this.sceneObject.setTransform({ position: pos, rotation: rot, scale: scl }, null);
  }

  private _applyStateTransform(
    state: StateDefinition | undefined,
    hadPreviousState: boolean,
    transition: TransitionConfig | undefined,
    enterFromOverride?: TransformOverride,
  ): void {
    const transform = state?.transform;

    this._ensureSavedTransform();

    // No override and no entry: nothing to animate unless a state's pose is
    // being reverted. Without a transform the target is the authored pose.
    if (!transform && !enterFromOverride && !hadPreviousState) return;

    const { pos: toPos, rot: toRot, scl: toScl } = Properties._overridePose(this._savedTransform!, transform);

    // enterFrom: snap the live transform to enterFrom's ABSOLUTE pose (omitted
    // fields fall back to the target pose) so the transition manager picks it up
    // as the starting "from". Used when an object becomes active (active:false →
    // true) — it animates "in from" enterFrom instead of from its current pose.
    if (enterFromOverride) {
      const fromTransform = Properties._computeTransformTargets(
        { pos: toPos, rot: toRot, scl: toScl },
        enterFromOverride,
      );
      this.sceneObject.transform.position.copyFrom(fromTransform.toPos);
      if (this.sceneObject.transform.rotationQuaternion) {
        this.sceneObject.transform.rotationQuaternion.copyFrom(fromTransform.toRot);
      } else {
        this.sceneObject.transform.rotationQuaternion = fromTransform.toRot.clone();
      }
      this.sceneObject.transform.scaling.copyFrom(fromTransform.toScl);
    }

    this.sceneObject.setTransform(
      { position: toPos, rotation: toRot, scale: toScl },
      transition ?? null,
    );
  }

  // States show and hide through the SceneObject's runtime override, never the
  // authored flag, so saving, duplicating or snapshotting mid-state keeps Base.
  private _applyStateActive(
    desiredActive: boolean | undefined,
    transition: TransitionConfig | undefined,
  ): void {
    // Always cancel pending deactivation — we're applying a new decision
    this._cancelDeactivation();

    if (desiredActive === undefined) {
      // Reverting: no state declares active anymore — back to authored
      this.sceneObject.setActiveOverride(undefined);
      return;
    }

    if (desiredActive) {
      // Activating — snap immediately so the object is visible during any
      // entry animation.
      this.sceneObject.setActiveOverride(true);
      return;
    }

    // Deactivating — defer until the transition completes so the object stays
    // visible while it animates out (matches Keyframes' previous behavior).
    // Include `delay` so an object with `{ duration: 0.5, delay: 0.2 }` stays
    // visible for the full 0.7 s, not just 0.5 s.
    const durationMs = (transition && isTimeTransition(transition))
      ? (transition.duration + (transition.delay ?? 0)) * 1000
      : 0;
    if (durationMs <= 0) {
      this.sceneObject.setActiveOverride(false);
      return;
    }
    // On the scene's clock: wall time, or the clock a host that steps the scene injected.
    this._deactivationTimer = getTransitionManager(this.scene).after(durationMs, () => {
      this._deactivationTimer = null;
      this.sceneObject.setActiveOverride(false);
    });
  }

  private _cancelDeactivation(): void {
    this._deactivationTimer?.();
    this._deactivationTimer = null;
  }

  private _setupTransformDriver(state: StateDefinition, config: PropertyTransition): void {
    this._ensureSavedTransform();

    const authored = this._savedTransform!;
    const transform = state.transform;
    if (!transform) return;

    const { toPos, toRot, toScl } = Properties._computeTransformTargets(authored, transform);

    this._transformDriver = {
      config,
      fromPos: authored.pos.clone(), fromRot: authored.rot.clone(), fromScl: authored.scl.clone(),
      toPos, toRot, toScl,
    };

    // Apply initial interpolated transform
    const driverValue = this._resolver.resolveDriverValue(config.driver);
    const progress = computeDriverProgress(driverValue, config.range, config.easing);
    this._applyInterpolatedTransform(progress);
  }

  private _updateTransformFromDriver(): void {
    if (!this._transformDriver) return;
    const driverValue = this._resolver.resolveDriverValue(this._transformDriver.config.driver);
    const progress = computeDriverProgress(driverValue, this._transformDriver.config.range, this._transformDriver.config.easing);
    this._applyInterpolatedTransform(progress);
  }

  private _applyInterpolatedTransform(progress: number): void {
    if (!this._transformDriver) return;
    const { fromPos, fromRot, fromScl, toPos, toRot, toScl } = this._transformDriver;

    // Strict equality: eased driver progress reaches exactly 1/0 at the range
    // ends; overshooting easings exceed them mid-range and must interpolate
    if (progress === 1) {
      this.sceneObject.setTransform({ position: toPos, rotation: toRot, scale: toScl }, null);
    } else if (progress === 0) {
      this.sceneObject.setTransform({ position: fromPos, rotation: fromRot, scale: fromScl }, null);
    } else {
      const pos = Vector3.Lerp(fromPos, toPos, progress);
      const rot = Quaternion.Slerp(fromRot, toRot, progress);
      const scl = Vector3.Lerp(fromScl, toScl, progress);
      this.sceneObject.setTransform({ position: pos, rotation: rot, scale: scl }, null);
    }
  }

  /** `_computeTransformTargets` as a pose; `under` itself when there is no override. */
  private static _overridePose(under: Pose, transform: TransformOverride | undefined): Pose {
    if (!transform) return under;
    const { toPos, toRot, toScl } = Properties._computeTransformTargets(under, transform);
    return { pos: toPos, rot: toRot, scl: toScl };
  }

  /**
   * Re-express an override over `under` in the frame `change` leads to, where
   * the pose under it is `underAfter`. Components it omitted take `under`'s
   * value for the change of frame, and stay omitted when they land on
   * `underAfter`'s; every component it keyed stays keyed. An override with a
   * bound component has no value to move and is returned as it is.
   */
  private static _reframeOverride(
    override: TransformOverride | undefined,
    under: Pose,
    underAfter: Pose,
    change: Matrix,
  ): TransformOverride | undefined {
    if (!override || hasBoundComponent(override)) return override;
    const moved = reframePose(Properties._overridePose(under, override), change);
    const out: TransformOverride = { ...override };
    if (override.position || !moved.pos.equalsWithEpsilon(underAfter.pos, POSE_EPSILON)) {
      out.position = [moved.pos.x, moved.pos.y, moved.pos.z];
    }
    if (override.rotation || Math.abs(Quaternion.Dot(moved.rot, underAfter.rot)) < 1 - POSE_EPSILON) {
      out.rotation = [moved.rot.x, moved.rot.y, moved.rot.z, moved.rot.w];
    }
    if (override.scale || !moved.scl.equalsWithEpsilon(underAfter.scl, POSE_EPSILON)) {
      out.scale = [moved.scl.x, moved.scl.y, moved.scl.z];
    }
    return out;
  }

  /**
   * Resolve a TransformOverride to its absolute local target pose. Each field is
   * the absolute value for that component; an omitted field falls back to the
   * authored value (CSS "no change by omission"). No delta arithmetic — the
   * override REPLACES per component. See ABSOLUTE_STATE_TRANSFORMS.md.
   */
  private static _computeTransformTargets(
    authored: { pos: Vector3; rot: Quaternion; scl: Vector3 },
    transform: NonNullable<StateDefinition['transform']>,
  ): { toPos: Vector3; toRot: Quaternion; toScl: Vector3 } {
    const num = (value: TransformComponent, authored: number): number =>
      Properties._transformComponent(value, authored);
    const toPos = transform.position
      ? new Vector3(
          num(transform.position[0], authored.pos.x),
          num(transform.position[1], authored.pos.y),
          num(transform.position[2], authored.pos.z))
      : authored.pos.clone();
    const toRot = transform.rotation
      ? new Quaternion(
          num(transform.rotation[0], authored.rot.x),
          num(transform.rotation[1], authored.rot.y),
          num(transform.rotation[2], authored.rot.z),
          num(transform.rotation[3], authored.rot.w))
      : authored.rot.clone();
    const toScl = transform.scale
      ? new Vector3(
          num(transform.scale[0], authored.scl.x),
          num(transform.scale[1], authored.scl.y),
          num(transform.scale[2], authored.scl.z))
      : authored.scl.clone();
    return { toPos, toRot, toScl };
  }

  /**
   * One transform component, resolved to a number.
   *
   * Components are `number | string` because they may be authored as
   * bindings; `_resolveValue` recurses into the tuple and has already
   * replaced anything that resolved. A string still standing here is a
   * binding nothing declares — the same "does this drive?" question
   * `bindingValues.drivesValue` answers for colors — so it falls back to the
   * authored component rather than writing NaN into the pose.
   */
  private static _transformComponent(value: number | string, authored: number): number {
    if (typeof value === 'number') return Number.isFinite(value) ? value : authored;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
    Logger.warn(`[Properties] transform component "${value}" does not resolve to a number — keeping the authored value`);
    return authored;
  }

  // ── Dependent behavior notification ──

  private _notifyDependentBehaviors(): void {
    this.sceneObject._resubscribeAndResolve();
    for (const behavior of this.sceneObject.getBehaviors()) {
      if (behavior !== this) {
        behavior._resubscribeAndResolve();
      }
    }
    const descendants = this.sceneObject.getDescendants();
    for (const desc of descendants) {
      desc._resubscribeAndResolve();
      for (const behavior of desc.getBehaviors()) {
        behavior._resubscribeAndResolve();
      }
    }
  }
}

/**
 * Create PropertiesData with declarations.
 */
export function createPropertiesData(
  declarations: PropertyDeclaration[] = [],
  states?: Record<string, StateDefinition>,
): PropertiesData {
  return {
    '@type': 'Properties',
    properties: declarations,
    states,
  };
}
